import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import { Logo } from '../ui/logo';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import type { JSX } from 'react';

/**
 * Main navigation bar component for the application.
 * Features include:
 * - Responsive design (desktop nav, mobile hamburger menu).
 * - Background blur and shadow effect on scroll.
 * - Active link highlighting.
 * - Scrolls to top on non-active link click.
 *
 * @returns The JSX element representing the Navbar.
 */
const Navbar = (): JSX.Element => {
  /** State to control the visibility of the mobile menu. */
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  /** State to track if the page has been scrolled down slightly. */
  const [scrolled, setScrolled] = useState(false);
  /** Current route location from wouter. */
  const [location] = useLocation();
  /** Ref to store the scroll event debounce timeout ID. */
  const scrollTimeout = useRef<number | null>(null);

  // Effect to handle scroll detection with debouncing
  useEffect(() => {
    /** Debounced scroll event handler. */
    const handleScroll = () => {
      // Clear previous timeout to prevent rapid state changes
      if (scrollTimeout.current) {
        window.clearTimeout(scrollTimeout.current);
      }

      // Set a small timeout to debounce the scroll event
      scrollTimeout.current = window.setTimeout(() => {
        setScrolled(window.scrollY > 20); // Add shadow/blur effect after scrolling 20px
      }, 10); // 10ms debounce
    };

    window.addEventListener('scroll', handleScroll, { passive: true }); // Use passive listener

    // Cleanup function
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        window.clearTimeout(scrollTimeout.current); // Clear timeout on unmount
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  /** Toggles the mobile menu open/closed state. */
  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  /** Closes the mobile menu. */
  const closeMenu = (): void => {
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 h-[72px] flex items-center ${
        scrolled
          ? 'bg-gradient-to-br from-black via-black to-gray-900 shadow-md shadow-primary/5'
          : 'bg-gradient-to-br from-black/80 via-black/80 to-gray-900/80 backdrop-blur-md'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link href="/" onClick={closeMenu} aria-label="Chatbox Labs Home">
              <Logo />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <NavLink href="/" label="Home" isActive={location === '/'} onClick={closeMenu} />
            <NavLink href="/ai-agents" label="AI Agents" isActive={location === '/ai-agents'} onClick={closeMenu} />
            <NavLink href="/resources" label="Resources" isActive={location === '/resources'} onClick={closeMenu} />
            <NavLink href="/about" label="About" isActive={location === '/about'} onClick={closeMenu} />
            {/** <NavLink href="/services" label="Services" isActive={location === '/services'} onClick={closeMenu} /> */}
            <NavLink href="/contact" label="Contact" isActive={location === '/contact'} onClick={closeMenu} />
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              className="text-foreground hover:text-primary"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 animate-in fade-in slide-in-from-top duration-300 absolute top-[72px] left-0 right-0 bg-background/95 shadow-lg">
            <nav className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col space-y-4">
              <NavLink href="/" label="Home" isActive={location === '/'} onClick={closeMenu} />
              <NavLink href="/ai-agents" label="AI Agents" isActive={location === '/ai-agents'} onClick={closeMenu} />
              <NavLink href="/resources" label="Resources" isActive={location === '/resources'} onClick={closeMenu} />
              <NavLink href="/about" label="About" isActive={location === '/about'} onClick={closeMenu} />
              {/** <NavLink href="/services" label="Services" isActive={location === '/services'} onClick={closeMenu} /> */}
              <NavLink href="/contact" label="Contact" isActive={location === '/contact'} onClick={closeMenu} />
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

/** Props for the NavLink component. */
interface NavLinkProps {
  /** The target URL for the link. */
  href: string;
  /** The text label displayed for the link. */
  label: string;
  /** Whether the link represents the currently active route. */
  isActive?: boolean;
  /** Optional click handler, often used to close the mobile menu. */
  onClick?: () => void;
}

/**
 * Renders a single navigation link with active state styling and hover effects.
 * Handles closing the mobile menu and scrolling to top on navigation.
 *
 * @param props - The properties for the NavLink.
 * @param props.href - The target URL.
 * @param props.label - The display text.
 * @param [props.isActive=false] - Whether the link is currently active.
 * @param [props.onClick] - Optional click handler.
 * @returns The JSX element representing the navigation link.
 */
const NavLink = ({ href, label, isActive = false, onClick }: NavLinkProps): JSX.Element => {

  /**
   * Handles the click event on the link.
   * Calls the provided onClick handler (e.g., to close mobile menu).
   */
  const handleClick = (): void => {
    // Scroll-to-top is handled globally by useScrollToTop in App.tsx

    // Call the original onClick if provided (e.g., to close mobile menu)
    if (onClick) {
      onClick();
    }
    // Wouter's Link component handles the actual navigation based on href
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
      className={`px-4 py-2 rounded-full font-medium transition-all duration-300 relative group ${
        isActive ? 'text-white font-semibold' : 'text-gray-300 hover:bg-white/20 hover:text-white'
      }`}
    >
      {label}
      {isActive && (
        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-white rounded-full motion-safe:transition-all motion-safe:duration-300"></span>
      )}
      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white rounded-full group-hover:w-full motion-safe:transition-all motion-safe:duration-300"></span>
    </Link>
  );
};

export default Navbar;
